import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { GET_US_OPT_OUT_CONFIG, UPDATE_US_OPT_OUT_CONFIG } from './graphql';

type VersionCondition = { op: string; value: string };
type UsOptOutDraft = {
  enabled: boolean;
  platform: string;
  versionConditions: VersionCondition[];
  noticeId: string;
};

const PLATFORM_OPTIONS = [
  { value: 'ALL', label: 'Both platforms' },
  { value: 'IOS', label: 'iOS only' },
  { value: 'ANDROID', label: 'Android only' },
];

const VERSION_OPS = ['>=', '>', '<=', '<', '==', '!='];
const VERSION_REGEX = /^\d+(\.\d+)*$/;
// Mirrors the server-side check: Didomi notice ids are short alphanumeric tokens.
const NOTICE_ID_REGEX = /^[A-Za-z0-9_-]{1,64}$/;

export default function UsOptOutCard() {
  const { data, loading, error, refetch } = useQuery(GET_US_OPT_OUT_CONFIG, {
    fetchPolicy: 'network-only',
  });
  const [updateConfig, { loading: saving }] = useMutation(UPDATE_US_OPT_OUT_CONFIG);

  const [draft, setDraft] = useState<UsOptOutDraft | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

  const saved = data?.liveOps?.usOptOutConfig;

  useEffect(() => {
    if (saved && !draft) {
      setDraft({
        enabled: saved.enabled,
        platform: saved.platform,
        versionConditions: saved.versionConditions.map(({ op, value }: VersionCondition) => ({ op, value })),
        noticeId: saved.noticeId ?? '',
      });
    }
  }, [saved, draft]);

  const setCondition = (index: number, patch: Partial<VersionCondition>) => {
    setDraft(previous =>
      previous
        ? {
            ...previous,
            versionConditions: previous.versionConditions.map((condition, i) =>
              i === index ? { ...condition, ...patch } : condition
            ),
          }
        : previous
    );
  };

  const addCondition = () => {
    setDraft(previous =>
      previous
        ? { ...previous, versionConditions: [...previous.versionConditions, { op: '>=', value: '' }] }
        : previous
    );
  };

  const removeCondition = (index: number) => {
    setDraft(previous =>
      previous
        ? { ...previous, versionConditions: previous.versionConditions.filter((_, i) => i !== index) }
        : previous
    );
  };

  const save = async () => {
    if (!draft) {
      return;
    }
    setFeedback(null);
    const invalid = draft.versionConditions.find(condition => !VERSION_REGEX.test(condition.value.trim()));
    if (invalid) {
      setFeedback({
        type: 'danger',
        message: `Version "${invalid.value}" is not valid - use digits and dots, e.g. 003.038.000.`,
      });
      return;
    }
    const noticeId = draft.noticeId.trim();
    if (noticeId.length > 0 && !NOTICE_ID_REGEX.test(noticeId)) {
      setFeedback({
        type: 'danger',
        message: `Notice ID "${draft.noticeId}" is not valid - paste only the Didomi notice id (letters and digits), not a URL.`,
      });
      return;
    }
    try {
      await updateConfig({
        variables: {
          input: {
            enabled: draft.enabled,
            platform: draft.platform,
            versionConditions: draft.versionConditions.map(condition => ({
              op: condition.op,
              value: condition.value.trim(),
            })),
            noticeId,
          },
        },
      });
      await refetch();
      setFeedback({ type: 'success', message: 'Saved. Clients pick it up on their next legal setup fetch (app launch).' });
    } catch (mutationError) {
      setFeedback({ type: 'danger', message: (mutationError as Error).message });
    }
  };

  if (loading && !data) {
    return <Spinner animation="border" className="m-4" />;
  }

  return (
    <Card className="mb-4">
      <Card.Header>
        US &quot;Do Not Sell/Share&quot; opt-out screen{' '}
        {saved &&
          (saved.enabled ? <Badge variant="success">Enabled</Badge> : <Badge variant="secondary">Disabled</Badge>)}
      </Card.Header>
      <Card.Body>
        <Alert variant="info" className="small">
          When ON, overage players in the <b>US and its territories</b> (not Canada) see the US opt-out consent screen
          instead of the standard CMP Accept/Decline screen. Country targeting is fixed server-side; the matchers below
          only stage the platform/version rollout. Applies on next app launch, no deploy.
        </Alert>
        {error && <Alert variant="danger">{error.message}</Alert>}
        {feedback && (
          <Alert variant={feedback.type} onClose={() => setFeedback(null)} dismissible>
            {feedback.message}
          </Alert>
        )}
        {draft && (
          <>
            <Row className="align-items-center mb-3">
              <Col xs="auto">
                <Form.Check
                  type="switch"
                  id="us-opt-out-enabled"
                  label={draft.enabled ? 'Enabled' : 'Disabled'}
                  checked={draft.enabled}
                  onChange={() =>
                    setDraft(previous => (previous ? { ...previous, enabled: !previous.enabled } : previous))
                  }
                />
              </Col>
              <Col xs="auto">
                <Form.Control
                  as="select"
                  size="sm"
                  value={draft.platform}
                  onChange={event => {
                    const platform = event.target.value;
                    setDraft(previous => (previous ? { ...previous, platform } : previous));
                  }}
                >
                  {PLATFORM_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Control>
              </Col>
            </Row>

            <b className="small">App version conditions (all must match)</b>
            <Table size="sm" borderless style={{ maxWidth: 420 }}>
              <tbody>
                {draft.versionConditions.map((condition, index) => (
                  <tr key={index}>
                    <td style={{ width: 90 }}>
                      <Form.Control
                        as="select"
                        size="sm"
                        value={condition.op}
                        onChange={event => setCondition(index, { op: event.target.value })}
                      >
                        {VERSION_OPS.map(op => (
                          <option key={op} value={op}>
                            {op}
                          </option>
                        ))}
                      </Form.Control>
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        placeholder="003.038.000"
                        value={condition.value}
                        onChange={event => setCondition(index, { value: event.target.value })}
                      />
                    </td>
                    <td style={{ width: 40 }}>
                      <Button size="sm" variant="outline-danger" onClick={() => removeCondition(index)}>
                        ×
                      </Button>
                    </td>
                  </tr>
                ))}
                {draft.versionConditions.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-muted small">
                      No conditions - all versions.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            <Button size="sm" variant="outline-secondary" onClick={addCondition}>
              Add version condition
            </Button>

            <hr />
            <Form.Group controlId="us-opt-out-notice-id" style={{ maxWidth: 420 }}>
              <Form.Label className="small mb-1">
                <b>US Didomi notice ID</b> (iOS + Android share one id)
              </Form.Label>
              <Form.Control
                size="sm"
                placeholder="empty = Didomi resolves the notice from the package name"
                value={draft.noticeId}
                onChange={event => {
                  const value = event.target.value;
                  setDraft(previous => (previous ? { ...previous, noticeId: value } : previous));
                }}
              />
              <Form.Text className="text-muted">
                Sent to US clients as <code>didomiNoticeId</code> while this feature is ON. Follow the CMP team&apos;s
                phases: <b>test id</b> from Mara while validating (never in a live rollout), the <b>permanent id</b>{' '}
                for the release that introduces the US notice, then <b>empty</b> only after Mara confirms the Didomi
                dashboard maps our package names to the new notice. Clearing it before that confirmation sends US
                players back to the old notice.
              </Form.Text>
            </Form.Group>

            <hr />
            <Button variant="success" disabled={saving} onClick={save}>
              {saving ? 'Saving...' : 'Save all settings'}
            </Button>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
